import { RefreshCw, Ticket } from "lucide-react";

import type { DocSummary } from "../../DocumentsOverviewStrip.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Badge, type BadgeProps } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { Checkbox } from "../../ui/checkbox.js";

export interface ChecklistItem { id: string; title: string; category: string; isMandatory: boolean; isCompleted: boolean }

export const REQUIRED_DOC_TYPES = [
  { type: "AADHAAR",       label: "Aadhaar",        aliases: ["AADHAAR"] },
  { type: "PAN",           label: "PAN",             aliases: ["PAN"] },
  { type: "BANK_DOCUMENT", label: "Bank Statement",  aliases: ["BANK_DOCUMENT"] },
] as const;
// Maps a checklist item's title to the required-doc entry it represents, so the
// checklist shows live upload/verify status inline.
export const CHECKLIST_DOC_TITLES: Record<string, { type: string; label: string; aliases: readonly string[] }> = {
  "Upload Aadhaar card": REQUIRED_DOC_TYPES[0],
  "Upload PAN card": REQUIRED_DOC_TYPES[1],
  "Upload bank document (cancelled cheque)": REQUIRED_DOC_TYPES[2],
  "Upload signed offer letter": { type: "OFFER_LETTER", label: "Offer letter", aliases: ["OFFER_LETTER"] },
  // Collected on wizard step 5 for India hires (HRMS-0627). Form 11 is listed
  // here even though its checklist row is optional — the badge still auto-ticks
  // when the employee does upload the form.
  "Upload degree certificate": { type: "DEGREE",    label: "Degree",    aliases: ["DEGREE"] },
  "Upload marksheet":          { type: "MARKSHEET", label: "Marksheet", aliases: ["MARKSHEET"] },
  "Form 11 (PF Declaration) submitted": { type: "FORM_11", label: "Form 11", aliases: ["FORM_11"] },
};

// Items whose state comes from data on this page (documents, verification
// records, consent, BGV, compensation, AD). They render as a read-only status
// badge; everything else is a manual tick. Must match the server's derived set
// in checkGatesAndActivate.
export const DERIVED_CHECKLIST_TITLES = new Set([
  "Data-protection notice accepted",
  "Add emergency contacts (min 2)",
  "Emergency contacts verified by HR",
  "References verified",
  "Background verification completed",
  "Compensation record locked",
  "Acknowledge employee handbook",
  "AD account provisioned",
  ...Object.keys(CHECKLIST_DOC_TITLES),
]);

const GROUPS: { category: string; label: string }[] = [
  { category: "DOCUMENT", label: "Documents" },
  { category: "TASK",     label: "Employee tasks" },
  { category: "APPROVAL", label: "HR verification" },
  { category: "SYSTEM",   label: "IT & access" },
];

// Where a derived item's underlying data lives on the profile page.
const ITEM_SECTION: Record<string, string> = {
  "Add emergency contacts (min 2)": "contacts",
};

const DOC_TONE = (status: string | undefined): BadgeProps["tone"] =>
  status === "VERIFIED" ? "success" : status === "REJECTED" ? "error" : status ? "warning" : "neutral";

export function OnboardingChecklist({
  items, isChecklistItemDone, docs, scrollToSection, recheckDoc, recheckingDocId,
  togglingItemId, toggleChecklistItem, navigate, employeeRouteId, readOnly,
}: {
  items: ChecklistItem[];
  isChecklistItemDone: (item: { title: string; isCompleted: boolean }) => boolean;
  docs: DocSummary[];
  scrollToSection: (sid: string) => void;
  recheckDoc: (docId: string) => Promise<void>;
  recheckingDocId: string | null;
  togglingItemId: string | null;
  toggleChecklistItem: (itemId: string, isCompleted: boolean) => Promise<void>;
  navigate: (path: string) => void;
  employeeRouteId: string | undefined;
  /** Onboarding completed: manual items can no longer be ticked. */
  readOnly: boolean;
}) {
  const mandatory = items.filter(i => i.isMandatory);
  const done = mandatory.filter(i => isChecklistItemDone(i)).length;

  const titleLink = (title: string, section: string | null) =>
    section
      ? <Button variant="ghost" size="sm" className="h-auto px-0 py-0 text-xs font-normal text-surface-700 hover:bg-transparent hover:underline" onClick={() => scrollToSection(section)}>{title}</Button>
      : <span className="text-xs text-surface-700">{title}</span>;

  const row = (item: ChecklistItem) => {
    const optional = !item.isMandatory && <span className="text-[11px] text-surface-400">(optional)</span>;
    const docType = CHECKLIST_DOC_TITLES[item.title];
    if (docType) {
      const doc = docs.find(d => docType.aliases.includes(d.type));
      return (
        <li key={item.id} className="flex flex-wrap items-center gap-2 py-1">
          <Badge tone={DOC_TONE(doc?.status)} className="shrink-0">{doc ? doc.status.replace(/_/g, " ").toLowerCase() : "not uploaded"}</Badge>
          {titleLink(item.title, "documents")}{optional}
          {doc?.status === "PENDING_UPLOAD" && (
            <AsyncButton
              onClick={() => recheckDoc(doc.id)}
              confirm={{ title: "Re-check this document now?", description: `Asks the server whether "${item.title}" has been uploaded since this page loaded. Nothing changes if it hasn't.`, actionText: "Re-check", tone: "info" }}
              disabled={recheckingDocId === doc.id} variant="outline" animated={false} size="sm"
            >
              <RefreshCw size={12} /> Recheck
            </AsyncButton>
          )}
        </li>
      );
    }
    if (DERIVED_CHECKLIST_TITLES.has(item.title)) {
      const ok = isChecklistItemDone(item);
      return (
        <li key={item.id} className="flex flex-wrap items-center gap-2 py-1">
          <Badge tone={ok ? "success" : "neutral"} className="shrink-0">{ok ? "done" : "pending"}</Badge>
          {titleLink(item.title, ITEM_SECTION[item.title] ?? null)}{optional}
        </li>
      );
    }
    const id = `chk-${item.id}`;
    return (
      <li key={item.id} className="flex flex-wrap items-center gap-2 py-1">
        <Checkbox
          id={id}
          checked={item.isCompleted}
          disabled={readOnly || togglingItemId === item.id}
          onCheckedChange={(v) => void toggleChecklistItem(item.id, v === true)}
        />
        <label htmlFor={id} className={item.isCompleted ? "text-xs text-surface-700" : "text-xs text-surface-500"}>{item.title}</label>
        {optional}
        {item.title === "IT asset request submitted" && !readOnly && (
          <Button variant="secondary" size="sm" className="h-6 px-2 text-[11px]"
            onClick={() => navigate(`/assets?tab=tickets&newTicket=1&employeeId=${employeeRouteId}&ticketType=NEW_EQUIPMENT`)}>
            <Ticket size={12} /> Raise IT Ticket
          </Button>
        )}
      </li>
    );
  };

  const known = new Set(GROUPS.map(g => g.category));
  const groups = [
    ...GROUPS.map(g => ({ ...g, items: items.filter(i => i.category === g.category) })),
    { category: "OTHER", label: "Other", items: items.filter(i => !known.has(i.category)) },
  ].filter(g => g.items.length > 0);

  return (
    <div className="border-b border-surface-100 py-2.5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] font-medium">Checklist</span>
        <span className="text-xs text-surface-500">{done} / {mandatory.length} mandatory complete · {items.length - mandatory.length} optional</span>
      </div>
      <div className="grid gap-x-6 gap-y-2 md:grid-cols-2">
        {groups.map(g => (
          <section key={g.category}>
            <h4 className="mt-1 mb-0.5 text-[11px] font-semibold tracking-wide text-surface-400 uppercase">{g.label}</h4>
            <ul>{g.items.map(row)}</ul>
          </section>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-surface-400">Badges follow the live data on this page; ticked boxes are manual.</p>
    </div>
  );
}
